import {
  Box,
  Chip,
  Icon,
  Image,
  Typography,
  useTheme,
} from "@outschool/backpack";
import {
  OSSubscriptions,
  centsPerLearnerPerMeeting,
} from "@outschool/business-rules";
import * as FilestackUrls from "@outschool/filestack-urls";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { faBirthdayCake, faCalendar, faUsersClass } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { addParamsToUrl } from "@outschool/routes";
import { measurePerformance, useProductClicked } from "@outschool/ui-analytics";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import {
  LeaderDetailsListing,
  LeaderDetailsMobileListing,
  isOneOnOne,
  useActivityString,
  usePriceDetailsString,
} from "@outschool/ui-components-website";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import {
  useDevicePixelRatio,
  useLinkComponent,
  useNavigation,
} from "@outschool/ui-utils";
import React from "react";

import * as Section from "../../../shared/Section";
import { useEsaUserWithoutPaidMembership } from "../../hooks/useEsaUserWithoutPaidMembership";
import { FreeForMembersChip } from "../Chips/FreeForMembersChip";
import { useIsActivitySavedByCurrentUser } from "../saving/graphql/hooks";
import { SaveHeart } from "../saving/SaveButton";
import { CoinIcon } from "../subscriptions/CoinIcon";
import { ActivityListingProps } from "./ActivityListing";
import { NextStartTime } from "./NextStartTime";

function FundingBadge() {
  const { t } = useTranslation("client\\components\\listings\\MobileListing");

  return (
    <Chip
      label={t("Funding Accepted")}
      size="small"
      sx={theme => ({
        color: theme.palette.grey[700],
        backgroundColor: theme.palette.grey[100],
      })}
    />
  );
}

function trackingObject(
  activity_uid: string,
  insertionId: string | null | undefined,
  trackingProperties: Record<any, any>,
) {
  return {
    trackingName: "Activity Impression",
    sharedProperties: {
      activity_uid,
      // Only include insertion_id when non-nullish to satisfy LinkProps type constraints
      ...(insertionId && { insertion_id: insertionId }),
      ...trackingProperties,
    },
    trackingTouchEventName: "Activity Impression Click",
    trackingViewEventName: "Activity Impression",
    trackingUniqueId: activity_uid,
  };
}

type MobileListingProps = {
  activity: ActivityListing_ListingFragment["activity"];
  nextSection: ActivityListing_ListingFragment["nextSection"];
  leader: ActivityListing_ListingFragment["leader"];
  sectionSearchFilters?: Section.SectionSearchFilters;
  trackingProperties: TrackingProperties;
  disabled: boolean;
  insertionId?: ActivityListing_ListingFragment["insertionId"];
  listingIndex?: number;
  listingDetailsOverride?: ActivityListingProps["listingDetailsOverride"];
  showCredits?: boolean;
  openInNewTab?: boolean;
  showFundingBadge?: boolean;
};

export const MobileListing = ({
  activity,
  nextSection,
  leader,
  sectionSearchFilters,
  trackingProperties,
  disabled,
  insertionId,
  listingIndex,
  listingDetailsOverride,
  showCredits,
  openInNewTab,
  showFundingBadge,
}: MobileListingProps) => {
  const Link = useLinkComponent();
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();
  const theme = useTheme();
  const trackProductClicked = useProductClicked();

  // @ts-ignore TS(2339): Property 'path' does not exist on type '{ __typena... Remove this comment to see the full error message
  let activityPath = activity.path;

  if (!!sectionSearchFilters) {
    activityPath = addParamsToUrl(activityPath, sectionSearchFilters);
  }

  const navigate = useNavigation();

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    trackProductClicked({ activity, leader }, listingIndex);

    if (openInNewTab) {
      e.preventDefault();
      navigate(activityPath, { newTab: true, hardNav: true });
    }
  };

  const { ageRangeString } = useActivityString();

  const isSaved = useIsActivitySavedByCurrentUser(activity);
  const pixelRatio = useDevicePixelRatio();
  const photo = FilestackUrls.imageUrl(activity.details.photo, {
    height: 90 * pixelRatio,
    width: 120 * pixelRatio,
    fit: "crop",
  });

  const {
    perClassString,
    classTypeString,
    perMeetingPriceString,
    detailedScheduleString,
  } = usePriceDetailsString();

  // TODO: Refactor to reduce duplication of layout styling
  if (listingDetailsOverride) {
    return (
      <Box
        flex
        id={activity.uid}
        sx={{
          borderRadius: "8px",
          backgroundColor: "#FFF",
          color: "grey.900",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: "180px",
          border: "1px solid",
          borderColor: "neutral.50",
          boxShadow:
            "0 1px 4px 0 rgba(0, 0, 0, 0.04), 0 0 8px 0 rgba(0, 0, 0, 0.04)",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        <Link
          to={activityPath}
          {...trackingObject(activity?.uid, insertionId, trackingProperties)}
          useLinkTracking
          tabIndex={disabled ? -1 : undefined}
          onClick={onLinkClick}
          style={{
            textDecoration: "none",
            color: "black",
            display: "block",
            width: "100%",
          }}
          trackViews={true}
        >
          {/* Row 1: Image, Title/Price, Heart */}
          <Box
            flex
            sx={{
              padding: "8px",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* Image (left) */}
            <Box
              sx={{
                flexShrink: 0,
                width: "80px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                [theme.breakpoints.down(400)]: {
                  width: "60px",
                },
              }}
            >
              {isOneOnOne(activity) ? (
                <UserHeadshotImage
                  user={leader}
                  size={60}
                  sx={{
                    border: "1px solid",
                    borderColor: "gray200",
                    [theme.breakpoints.down(400)]: {
                      width: "48px",
                      height: "48px",
                    },
                  }}
                />
              ) : (
                <Image
                  width="80px"
                  height="60px"
                  sx={{
                    width: "80px",
                    height: "60px",
                    borderRadius: "8px",
                    objectFit: "cover",
                    display: "block",
                    [theme.breakpoints.down(400)]: {
                      width: "60px",
                      height: "48px",
                    },
                  }}
                  src={photo}
                  // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                  onLoad={
                    listingIndex === 0
                      ? () => {
                          measurePerformance("os-page-load", {
                            source: "image",
                          });
                        }
                      : null
                  }
                />
              )}
            </Box>

            {/* Title (middle) */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    lineHeight: 1.2,
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    flex: 1,
                    [theme.breakpoints.up("md")]: {
                      fontSize: "1.3rem",
                    },
                  }}
                >
                  {activity.titleTranslated}
                </Typography>
                <SaveHeart
                  isSet={isSaved}
                  sx={{
                    fontSize: 20,
                    padding: "12px",
                    flexShrink: 0,
                  }}
                  activity={activity}
                  leader={leader}
                  disabled={disabled}
                />
              </Box>
            </Box>
          </Box>

          {/* Row 2: Teacher name */}
          <Box
            sx={{
              padding: "0 8px 4px 8px",
            }}
          >
            <LeaderDetailsMobileListing leader={leader} />
          </Box>
        </Link>
        {listingDetailsOverride}

        {/* Row 3: Attributes and next session in chips (for override cases) */}
        <Box
          sx={{
            padding: "4px 8px 8px 8px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            flex: 1,
            justifyContent: "flex-end",
            [theme.breakpoints.down(400)]: {
              padding: "4px 8px 8px 8px",
            },
          }}
        >
          {/* Attributes row - horizontally scrollable chips */}
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              overflowX: "auto",
              paddingBottom: "2px",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE and Edge
            }}
          >
            <Chip
              size="small"
              label={
                <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                  <Icon icon={faUsersClass} sx={{ fontSize: "12px" }} />
                  <Typography variant="caption">
                    {classTypeString(activity)}
                  </Typography>
                </Box>
              }
              sx={{
                flexShrink: 0,
                backgroundColor: "grey.50",
                color: "grey.700",
              }}
            />
            <Chip
              size="small"
              label={
                <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                  <Icon icon={faCalendar} sx={{ fontSize: "12px" }} />
                  <Typography variant="caption">
                    {detailedScheduleString(activity)}
                  </Typography>
                </Box>
              }
              sx={{
                flexShrink: 0,
                backgroundColor: "grey.50",
                color: "grey.700",
              }}
            />
            <Chip
              size="small"
              label={
                <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                  <Icon icon={faBirthdayCake} sx={{ fontSize: "12px" }} />
                  <Typography variant="caption">
                    {ageRangeString(activity, false)}
                  </Typography>
                </Box>
              }
              sx={{
                flexShrink: 0,
                backgroundColor: "grey.50",
                color: "grey.700",
              }}
            />
          </Box>

          {/* Next session starts - in a chip if there's session info */}
          {(nextSection?.start_time ||
            nextSection?.nextOngoingMeeting?.start_time) && (
            <Chip
              size="small"
              label={
                <Box
                  flex
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box flex sx={{ alignItems: "center", gap: "8px" }}>
                    <Icon
                      icon={faCalendar}
                      sx={{
                        fontSize: "16px",
                        color: "grey.600",
                      }}
                    />
                    <Box
                      sx={{
                        color: "grey.700",
                        fontWeight: "500",
                        "& *": {
                          color: "inherit !important",
                          fontSize: "14px !important",
                          fontWeight: "500 !important",
                        },
                      }}
                    >
                      <NextStartTime
                        nextSectionStartTime={nextSection?.start_time}
                        nextOngoingMeetingStartTime={
                          nextSection?.nextOngoingMeeting?.start_time
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              }
              sx={{
                width: "100%",
                backgroundColor: "grey.50",
                color: "grey.700",
                justifyContent: "flex-start",
                paddingY: "4px",
                paddingX: "8px",
                "& .MuiChip-label": {
                  paddingLeft: 0,
                  paddingRight: 0,
                  width: "100%",
                  textAlign: "left",
                },
              }}
            />
          )}
        </Box>
      </Box>
    );
  } else {
    return (
      <Link
        to={activityPath}
        {...trackingObject(activity?.uid, insertionId, trackingProperties)}
        useLinkTracking
        tabIndex={disabled ? -1 : undefined}
        onClick={onLinkClick}
        style={{ textDecoration: "none", display: "block", width: "100%" }}
        trackViews={true}
        data-test-id="activity-listing-link"
      >
        <Box
          flex
          id={activity.uid}
          sx={{
            borderRadius: "8px",
            backgroundColor: "#FFF",
            color: "grey.900",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: "180px",
            border: "1px solid",
            borderColor: "neutral.50",
            boxShadow:
              "0 1px 4px 0 rgba(0, 0, 0, 0.04), 0 0 8px 0 rgba(0, 0, 0, 0.04)",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowWrap: "break-word",
            wordBreak: "break-word",
            "&:hover": {
              boxShadow: disabled
                ? "0 1px 4px 0 rgba(0, 0, 0, 0.04), 0 0 8px 0 rgba(0, 0, 0, 0.04)"
                : "0 2px 10px rgba(0, 0, 0, 0.25)",
            },
          }}
        >
          {/* Row 1: Image, Title/Price, Heart */}
          <Box
            flex
            sx={{
              padding: "12px",
              alignItems: "center",
              gap: "12px",
              overflow: "hidden",
              [theme.breakpoints.down(400)]: {
                padding: "8px",
                gap: "8px",
              },
            }}
          >
            {/* Image (left) */}
            <Box
              sx={{
                flexShrink: 0,
                width: "80px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isOneOnOne(activity) ? (
                <UserHeadshotImage
                  user={leader}
                  size={60}
                  sx={{ border: "1px solid", borderColor: "gray200" }}
                />
              ) : (
                <Image
                  width="80px"
                  height="60px"
                  sx={{
                    width: "80px",
                    height: "60px",
                    borderRadius: "8px",
                    objectFit: "cover",
                    display: "block",
                  }}
                  src={photo}
                  // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                  onLoad={
                    listingIndex === 0
                      ? () => {
                          measurePerformance("os-page-load", {
                            source: "image",
                          });
                        }
                      : null
                  }
                />
              )}
            </Box>

            {/* Title and Price (middle, stacked) */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    lineHeight: 1.2,
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    flex: 1,
                    [theme.breakpoints.up("md")]: {
                      fontSize: "1.3rem",
                    },
                  }}
                >
                  {activity.titleTranslated}
                </Typography>
                <SaveHeart
                  isSet={isSaved}
                  sx={{
                    fontSize: 20,
                    padding: "12px",
                    flexShrink: 0,
                  }}
                  activity={activity}
                  leader={leader}
                  disabled={disabled}
                />
              </Box>

              {showFundingBadge && (
                <Box>
                  <FundingBadge />
                </Box>
              )}
              {activity.isIncludedWithMembership && (
                <Box>
                  <FreeForMembersChip />
                </Box>
              )}

              <Box
                flex
                sx={{
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography variant="body2" emphasized color={"grey.900"}>
                  {showCredits ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(3),
                      }}
                    >
                      <CoinIcon
                        fillColor={theme.palette.warning.main}
                        strokeColor={theme.palette.warning[700] as string}
                        width={18}
                        height={18}
                      />
                      {activity.isIncludedWithMembership &&
                      !isEsaUserWithoutPaidMembership
                        ? "0"
                        : OSSubscriptions.priceCentsToCredits(
                            centsPerLearnerPerMeeting(activity),
                          )}
                    </Box>
                  ) : (
                    perMeetingPriceString(activity)
                  )}
                </Typography>
                <Typography variant="body2" color={"grey.600"}>
                  {showCredits ? "credits " : ""}
                  {perClassString(activity)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Row 2: Teacher name stacked above star rating */}
          <Box
            sx={{
              padding: "0 12px 4px 12px",
            }}
          >
            <LeaderDetailsListing
              leader={leader}
              sx={{
                width: "100%",
                maxWidth: "100%",
              }}
            />
          </Box>

          {/* Row 3: Attributes and next session in chips */}
          <Box
            sx={{
              padding: "4px 8px 8px 8px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              flex: 1,
              justifyContent: "flex-end",
              [theme.breakpoints.down(400)]: {
                padding: "4px 8px 8px 8px",
              },
            }}
          >
            {/* Attributes row - horizontally scrollable chips */}
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                overflowX: "auto",
                paddingBottom: "2px",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE and Edge
              }}
            >
              <Chip
                size="small"
                label={
                  <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                    <Icon icon={faUsersClass} sx={{ fontSize: "12px" }} />
                    <Typography variant="caption">
                      {classTypeString(activity)}
                    </Typography>
                  </Box>
                }
                sx={{
                  flexShrink: 0,
                  backgroundColor: "grey.50",
                  color: "grey.700",
                }}
              />
              <Chip
                size="small"
                label={
                  <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                    <Icon icon={faCalendar} sx={{ fontSize: "12px" }} />
                    <Typography variant="caption">
                      {detailedScheduleString(activity)}
                    </Typography>
                  </Box>
                }
                sx={{
                  flexShrink: 0,
                  backgroundColor: "grey.50",
                  color: "grey.700",
                }}
              />
              <Chip
                size="small"
                label={
                  <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                    <Icon icon={faBirthdayCake} sx={{ fontSize: "12px" }} />
                    <Typography variant="caption">
                      {ageRangeString(activity, false)}
                    </Typography>
                  </Box>
                }
                sx={{
                  flexShrink: 0,
                  backgroundColor: "grey.50",
                  color: "grey.700",
                }}
              />
            </Box>

            {/* Next session starts - in a chip if there's session info */}
            {(nextSection?.start_time ||
              nextSection?.nextOngoingMeeting?.start_time) && (
              <Chip
                size="small"
                label={
                  <Box
                    flex
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      flex
                      sx={{
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Icon
                        icon={faCalendar}
                        sx={{
                          fontSize: "16px",
                          color: "grey.600",
                        }}
                      />
                      <Box
                        sx={{
                          color: "grey.700",
                          fontWeight: "500",
                          "& *": {
                            color: "inherit !important",
                            fontSize: "14px !important",
                            fontWeight: "500 !important",
                          },
                        }}
                      >
                        <NextStartTime
                          nextSectionStartTime={nextSection?.start_time}
                          nextOngoingMeetingStartTime={
                            nextSection?.nextOngoingMeeting?.start_time
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                }
                sx={{
                  width: "100%",
                  backgroundColor: "grey.50",
                  color: "grey.700",
                  justifyContent: "flex-start",
                  paddingY: "16px",
                  paddingX: "8px",
                  "& .MuiChip-label": {
                    width: "100%",
                    textAlign: "left",
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Link>
    );
  }
};
